import { useCallback, useRef, useState } from 'react';
import { foundation } from '../../ui/tokens';
import { nextRadioIndex } from '../../utils/ui/radioGroupNavigation';
import { supportsHover } from '../../utils/ui/supportsHover';
import { usePickRowGeometry, pickNameStyle, pickSubStyle } from './selectList/pickRowGeometry';

const { color, radii, fontSize, fontFamily, fontWeight, shadow } = foundation;

// Keyboard focus: the app's field focus canon (brand edge + soft 3px accent[100] halo), drawn INSET so the
// rows' box never clips it. Shown on :focus-visible only, so a finger tap never paints it.
const FOCUS_RING = `inset 0 0 0 1px ${color.brand.primary}, inset ${shadow.fieldFocus}`;
const isFocusVisible = (el) => { try { return el.matches(':focus-visible'); } catch (e) { return false; } };

// One shared look for every pick-one list (founder, 2026-10-28): flat rows edge to edge, hairline
// dividers, no fill on the picked row (the filled radio + brand-blue label carry it), the radio
// on the right. Inside a pop-up the rows sit in ONE light bordered box; `bare` drops that box for
// full-screen cards (the card is the box).
const boxStyle = { display: 'flex', flexDirection: 'column', border: `1px solid ${color.neutral.border}`, borderRadius: radii.md, background: color.neutral.surface, overflow: 'hidden' };
const radioDot = (on, box) => ({
  width: box, height: box, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
  border: `2px solid ${on ? color.brand.primary : color.neutral.borderStrong}`,
  background: on ? `radial-gradient(circle, ${color.brand.primary} 0 4px, ${color.neutral.surface} 5px)` : color.neutral.surface,
});

/**
 * A short, FIXED set of choices rendered inline — every option visible, no
 * popover.
 *
 * *** WHY THIS EXISTS AT ALL (founder, 2026-09-01). *** A dropdown for four
 * fixed options inside a modal is the wrong control: it hides three of the four
 * answers behind a tap, opens a second layer above a dialog that already has a
 * scrim and a keyboard-aware reframe, and forces the caller to guard Escape and
 * Enter against the popover's own document listeners (see the two regressions
 * `DeclineEstimateModal` used to carry). The customer-facing decline sheet
 * already renders these same four reasons as inline rows and was approved that
 * way, so admin and customer were disagreeing on presentation for identical
 * data. A `FilterSelect` still earns its place on a long or unbounded list —
 * this is for the short, closed ones.
 *
 * *** NO PRE-SELECTED DEFAULT, EVER. *** `value` starts null/'' and the caller
 * gates its primary button on a real choice. A pre-filled reason means a
 * customer's recorded reason can be one nobody picked — the same rule the
 * customer sheet states in its own words.
 *
 * BEHAVIOUR CONTRACT (`role="radio"` promises it, so we implement it):
 *  • roving tabindex — exactly ONE tab stop for the whole group
 *  • arrows move AND select; Home/End jump to the ends
 *  • the index math is `nextRadioIndex`, shared with the customer sheet
 *  • 44px minimum row height — these are thumb targets on a phone
 *
 * Buttons rather than `<input type="radio">`: the visual is a bordered ROW with
 * a custom dot, and the button form keeps the markup honest instead of hiding a
 * native input under a label.
 *
 * @param {Object} props
 * @param {string|null} props.value        currently selected option value
 * @param {Array<{value:string,label:string,leading?:React.ReactNode,description?:string,disabled?:boolean}>} props.options
 *   `disabled` (optional) mutes ONE row: not selectable, aria-disabled, skipped by the arrow keys.
 *   `leading` sits first (left of the label);
 *   `description` is a quiet second line under the label.
 *   `trailing` (optional node) sits right-aligned on the label's row (e.g. a price).
 * @param {boolean} [props.bare]  no outer box (inside a full-screen card, which is the box)
 * @param {boolean} [props.touch]  force 48px rows (defaults to `useResponsiveLayout().isTouch`)
 * @param {string}  [props.ariaLabel]  group label when there is no visible label element
 * @param {string}  [props.optionTestIdPrefix]  rows get `${prefix}-${value}` instead of `${testIdPrefix}-option-${value}`
 * @param {(value:string)=>void} props.onChange
 * @param {boolean} [props.disabled]
 * @param {string}  [props.labelledBy]     id of the VISIBLE label element
 * @param {string}  [props.describedBy]    id of an error/hint element
 * @param {boolean} [props.required]       renders aria-required on the group
 * @param {string}  [props.testIdPrefix]   `${prefix}-option-${value}` per row
 */
export default function InlineRadioGroup({
  value,
  options = [],
  onChange,
  disabled = false,
  labelledBy,
  describedBy,
  required = false,
  testIdPrefix,
  bare = false,
  touch,
  optionTestIdPrefix,
  ariaLabel,
}) {
  const g = usePickRowGeometry(touch);
  const isTouch = g.touch;
  const groupRef = useRef(null);
  // One scalar each (a pointer is over one row, focus is in one row).
  const [hovered, setHovered] = useState(null);
  const [focusVisible, setFocusVisible] = useState(null);
  // Read at render, never latched: a tap on iOS synthesises a mouseenter that can stick, which would leave
  // an unselected row tinted after the finger has gone.
  const canHover = supportsHover() && !isTouch;

  const handleKeyDown = useCallback((event) => {
    if (disabled) return;
    const current = options.findIndex((o) => o.value === value);
    let next = nextRadioIndex(event.key, current, options.length);
    // `null` = not a navigation key. Returning early WITHOUT preventDefault is
    // the whole reason the helper returns null instead of a number: swallowing
    // Tab would trap focus in the group and swallowing Escape would strand the
    // dialog.
    if (next === null) return;
    event.preventDefault();
    // Per-option `disabled` (KAN-196): keep stepping the same way past rows that can't be chosen.
    const step = event.key === 'Home' ? 1 : event.key === 'End' ? -1 : (event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1);
    for (let i = 0; i < options.length && options[next].disabled; i += 1) next = (next + step + options.length) % options.length;
    if (options[next].disabled) return;
    onChange?.(options[next].value);
    const node = groupRef.current?.querySelectorAll('[role="radio"]')[next];
    if (node && typeof node.focus === 'function') node.focus();
  }, [disabled, options, value, onChange]);

  if (!options.length) return null;

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : ariaLabel}
      aria-describedby={describedBy}
      aria-required={required ? 'true' : undefined}
      onKeyDown={handleKeyDown}
      data-testid={testIdPrefix}
      style={bare ? { display: 'flex', flexDirection: 'column' } : boxStyle}
    >
      {options.map((option, index) => {
        const selected = value === option.value;
        const rowOff = disabled || option.disabled === true;
        // Roving tabindex: exactly ONE stop. With nothing selected the first
        // row is the entry point, so the group stays keyboard-reachable.
        const firstOn = options.findIndex((o) => !o.disabled);
        const roving = selected || (!value && index === firstOn) ? 0 : -1;
        // Opt-in (option.descriptionOpaqueWhenDisabled): dim only the label/dot/leading so a
        // disabled row's description (e.g. a warning hint) keeps full contrast.
        const keepDesc = rowOff && option.descriptionOpaqueWhenDisabled === true && Boolean(option.description);
        const dim = keepDesc ? { opacity: 0.55 } : null; // dark raises the dim (index.css [data-cvx-dim]) so muted text stays >= 4.5:1
        const dot = <span aria-hidden="true" style={{ ...dim, ...radioDot(selected, g.box) }} />;
        // The control speaks (KAN-1104): no fill on the picked row — filled radio + brand-blue semibold label.
        const nameStyle = { ...pickNameStyle, transition: 'color 160ms ease', ...(selected ? { color: color.brand.text } : null) };
        const hoverOn = !selected && canHover && hovered === index;
        const isLast = index === options.length - 1;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={roving}
            disabled={disabled}
            aria-disabled={option.disabled ? 'true' : undefined}
            aria-label={option.ariaLabel}
            title={option.title}
            onClick={() => { if (!option.disabled) onChange?.(option.value); }}
            onMouseEnter={() => { if (!option.disabled) setHovered(index); }}
            onMouseLeave={() => setHovered((c) => (c === index ? null : c))}
            onFocus={(e) => { if (isFocusVisible(e.currentTarget)) setFocusVisible(index); }}
            onBlur={() => setFocusVisible((c) => (c === index ? null : c))}
            data-testid={optionTestIdPrefix ? `${optionTestIdPrefix}-${option.value}` : (testIdPrefix ? `${testIdPrefix}-option-${option.value}` : undefined)}
            {...(hoverOn ? { 'data-cvx-hover-fill': '' } : null)}
            {...(rowOff && !keepDesc ? { 'data-cvx-dim': '' } : null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: g.gap,
              width: '100%',
              // 44px: these are thumb targets, and the modal opens on a phone
              // far more often than on a desktop.
              minHeight: g.minHeight,
              padding: `${g.padY} ${g.edge}px`,
              textAlign: 'left',
              border: 'none',
              borderBottom: isLast ? 'none' : g.divider,
              borderRadius: 0,
              background: 'transparent',
              ...(hoverOn ? { '--cvx-row-hover-fill': color.neutral.surfaceMuted } : null),
              boxShadow: focusVisible === index ? FOCUS_RING : 'none',
              WebkitTapHighlightColor: 'transparent',
              color: color.neutral.text,
              fontFamily: fontFamily.sans,
              fontSize: fontSize.sm,
              fontWeight: fontWeight.semibold,
              cursor: rowOff ? 'not-allowed' : 'pointer',
              opacity: rowOff && !keepDesc ? 0.55 : 1,
              boxSizing: 'border-box',
            }}
          >
            {dim && option.leading ? <span style={{ display: 'inline-flex', ...dim }}>{option.leading}</span> : option.leading}
            {option.description ? (
              <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', flex: option.trailing ? 1 : undefined }}>
                <span {...(dim ? { 'data-cvx-dim': '' } : null)} style={{ ...nameStyle, ...dim }}>{option.label}</span>
                <span style={pickSubStyle(g.lineGap)}>
                  {option.description}
                </span>
              </span>
            ) : (
              <span {...(dim ? { 'data-cvx-dim': '' } : null)} style={{ ...nameStyle, ...dim }}>{option.label}</span>
            )}
            {option.trailing ? <span style={{ marginLeft: 'auto', ...dim }}>{option.trailing}</span> : null}
            <span style={{ marginLeft: option.trailing ? 0 : 'auto', display: 'inline-flex' }}>{dot}</span>
          </button>
        );
      })}
    </div>
  );
}
